import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import userReducer from "./UserSlice";
import { clearUser } from "./UserSlice";
import subscriptionReducer from "./SubscriptionSlice";
import { reset as resetSubscription } from "./SubscriptionSlice";
import teamReducer from "./TeamSlice";
import { reset as resetTeam } from "./TeamSlice";
import { removeToken } from "../utils/token";
import rolesReducer from "./RolesSlice";
import { reset as resetRoles } from "./RolesSlice";
import { clearPersistData } from "../utils/clearPersistData";
const ENABLE_PERSISTENCE = true;

let finalUserReducer = userReducer;

if (ENABLE_PERSISTENCE) {
  try {
    const persistConfig = {
      key: "dealboxx-vendor",
      storage,
      whitelist: [
        "user",
        "isAuthenticated",
        "userPermissions",
        "userRole",
        "isMainVendor",
      ],
      // Add debug logging for persistence
      debug: process.env.NODE_ENV === 'development',
    };
    finalUserReducer = persistReducer(persistConfig, userReducer);
  } catch (error) {
    console.warn("Persistence disabled due to error:", error);
    finalUserReducer = userReducer;
  }
}

export const store = configureStore({
  reducer: {
    user: finalUserReducer,
    subscription: subscriptionReducer,
    team: teamReducer,
    roles: rolesReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat((store) => (next) => (action) => {
      // Log all actions for debugging
      if (action.type && (action.type.includes('clear') || action.type.includes('reset'))) {
        console.log('Dispatching action:', action.type, action);
      }
      return next(action);
    }),
  devTools: process.env.NODE_ENV !== "production",
});

export const persistor = ENABLE_PERSISTENCE ? persistStore(store, null, () => {
  console.log('Persistence rehydration completed');
  const state = store.getState();
  console.log('State after rehydration:', {
    user: state.user,
    isAuthenticated: state.user?.isAuthenticated
  });
}) : null;

// Utility function to check if user is properly authenticated
export const isUserAuthenticated = () => {
  const state = store.getState();
  const user = state.user;
  
  // Check if user exists and is authenticated
  if (!user || !user.user || !user.isAuthenticated) {
    return false;
  }
  
  // Check if token exists
  const token = localStorage.getItem('dealboxx_token');
  if (!token) {
    return false;
  }
  
  return true;
};

// Utility function to log current state for debugging
export const logCurrentState = () => {
  const state = store.getState();
  console.log('Current Redux State:', {
    user: state.user,
    subscription: state.subscription,
    team: state.team,
    roles: state.roles
  });
  return state;
};

// Comprehensive function to clear all user data
export const clearAllUserData = () => {
  const dispatch = store.dispatch;
  
  console.log('Starting to clear all user data...');
  
  // Clear all Redux state
  console.log('Dispatching clearUser...');
  dispatch(clearUser());
  
  console.log('Dispatching clearSubscription...');
  dispatch(resetSubscription());
  
  console.log('Dispatching resetTeam...');
  dispatch(resetTeam());
  
  console.log('Dispatching resetRoles...');
  dispatch(resetRoles());
  
  // Log the state after clearing to verify
  setTimeout(() => {
    const state = store.getState();
    console.log('State after clearing:', {
      user: state.user,
      subscription: state.subscription,
      team: state.team,
      roles: state.roles
    });
  }, 100);
  
  console.log('Clearing localStorage and sessionStorage...');
  if (!ENABLE_PERSISTENCE) {
    clearPersistData();
  } else {
    localStorage.removeItem("vendorData");
    localStorage.removeItem("dealboxx_token");
  }
  sessionStorage.clear();
  
  Object.keys(localStorage).forEach(key => {
    if ((key.includes('dealboxx') || key.includes('vendor') || key.includes('user')) 
        && !key.includes('persist:')) {
      localStorage.removeItem(key);
    }
  });

  // Clear token
  if (localStorage.getItem("dealboxx_token")) {
    removeToken();
  }
  
  // Only purge if persistor exists (when persistence is enabled)
  if (persistor) {
    persistor.purge();
  }
  
  console.log('All user data cleared successfully');
};

// Test function to manually test reset actions
export const testResetActions = () => {
  const dispatch = store.dispatch;
  console.log('Testing reset actions...');
  
  // Test each action individually
  console.log('Testing clearUser...');
  dispatch(clearUser());
  
  console.log('Testing resetSubscription...');
  dispatch(resetSubscription());
  
  console.log('Testing resetTeam...');
  dispatch(resetTeam());
  
  console.log('Testing resetRoles...');
  dispatch(resetRoles());
  
  // Log state after each action
  setTimeout(() => {
    const state = store.getState();
    console.log('State after test reset:', {
      user: state.user,
      subscription: state.subscription,
      team: state.team,
      roles: state.roles
    });
  }, 100);
};

export const logout = () => (dispatch) => {
  console.log('Logout function called');
  
  // Clear all user data
  clearAllUserData();
  
  setTimeout(() => {
    const state = store.getState();
    console.log('State after logout:', {
      user: state.user,
      subscription: state.subscription,
      team: state.team,
      roles: state.roles
    });
    window.location.href = "/";
  }, 500);
};

if (typeof window !== 'undefined') {
  window.testResetActions = testResetActions;
  window.logCurrentState = () => {
    const state = store.getState();
    console.log('Current Redux State:', state);
    return state;
  };
}